// Post-login return path. RequireAuth hands the page the visitor asked for
// (location state); after sign-in we go back there — but ONLY to an in-app
// page. Anything else (another origin, a protocol-relative "//host", a
// backslash trick, a public page) falls back to /app, so the login page can
// never be turned into an open redirect.

export interface ReturnLocation {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
}

/** The validated in-app target ("/app" or "/app/…", with search + hash), or
 *  null when `from` is missing or not an in-app path. */
export function safeNextPath(from: unknown): string | null {
  let path: string;
  if (typeof from === "string") {
    path = from;
  } else if (from && typeof from === "object") {
    const loc = from as ReturnLocation;
    if (typeof loc.pathname !== "string") return null;
    path =
      loc.pathname +
      (typeof loc.search === "string" ? loc.search : "") +
      (typeof loc.hash === "string" ? loc.hash : "");
  } else {
    return null;
  }
  // Exactly "/app" or under "/app/"; no scheme, no "//", no backslashes, no
  // control characters, no dot segments that could climb out of /app.
  if (!(path === "/app" || path.startsWith("/app/") || path.startsWith("/app?") || path.startsWith("/app#"))) {
    return null;
  }
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f]/.test(path)) return null;
  const pathname = path.split(/[?#]/, 1)[0];
  if (pathname.includes("//")) return null;
  if (pathname.split("/").some((seg) => seg === ".." || seg === ".")) return null;
  return path;
}

/** Where to land after a successful sign-in. */
export function postLoginPath(from: unknown): string {
  return safeNextPath(from) ?? "/app";
}
